/**
 * MySuggestionsWidget
 *
 * Dashboard homepage widget showing the user's own suggestions.
 * Simplified view that links to the full My Suggestions page.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 20, 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MessageSquare,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  CheckCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface Suggestion {
  id: string;
  suggestionType: string;
  title: string;
  status: string;
  createdAt: string;
}

const statusConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  pending: {
    icon: <Clock className="h-3 w-3" />,
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    label: 'Pending'
  },
  reviewed: {
    icon: <CheckCircle className="h-3 w-3" />,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    label: 'Reviewed'
  },
  implemented: {
    icon: <CheckCircle2 className="h-3 w-3" />,
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    label: 'Implemented'
  },
  dismissed: {
    icon: <XCircle className="h-3 w-3" />,
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    label: 'Dismissed'
  },
};

export function MySuggestionsWidget() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchSuggestions();
    }
  }, [selectedSite?.id]);

  const fetchSuggestions = async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/content-evaluations/my-suggestions?siteId=${selectedSite.id}`);
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.data || []);
      } else {
        setError('Failed to load suggestions');
      }
    } catch (err) {
      console.error('[MySuggestionsWidget] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;

  if (!selectedSite?.id) {
    return null;
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            My Suggestions
          </CardTitle>
          <Badge variant="secondary">Error</Badge>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchSuggestions}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          My Suggestions
        </CardTitle>
        {loading ? (
          <Skeleton className="h-5 w-16" />
        ) : (
          <Badge variant={pendingCount > 0 ? 'default' : 'secondary'}>
            {pendingCount} pending
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        ) : suggestions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <MessageSquare className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No suggestions submitted yet.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {suggestions.slice(0, 3).map((suggestion) => {
              const config = statusConfig[suggestion.status] || statusConfig.pending;
              return (
                <div
                  key={suggestion.id}
                  className="flex items-center gap-2 p-2 rounded-md"
                >
                  <Badge className={`${config.color} text-xs gap-1 shrink-0`}>
                    {config.icon}
                    {config.label}
                  </Badge>
                  <span className="flex-1 text-sm truncate" title={suggestion.title}>
                    {suggestion.title}
                  </span>
                </div>
              );
            })}
            {suggestions.length > 3 && (
              <p className="text-xs text-muted-foreground text-center">
                +{suggestions.length - 3} more
              </p>
            )}
          </div>
        )}

        <Button
          variant="ghost"
          className="w-full mt-3"
          onClick={() => navigate('/my-suggestions')}
        >
          View All
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default MySuggestionsWidget;
